/**
 * Innovation Hub admin API client (pcb-innovation-api 0.4.0, live 2026-10-16).
 * Contract: pcb-innovation-hub/docs/operations/2026-10-16-hub-admin-portal-contract.md.
 *
 * Sends ONLY the shared backend token (auth/tokens.ts). Until BT grants consent there is
 * no such token and every call rejects with ConsentWaitingError, so no request is made.
 * Admin check is the Hub's own (hub.admin.upns); a non-admin gets 403 NOT_ADMIN.
 */

import { getPlatformToken, CONSENT_WAITING_MESSAGE } from '@/auth/tokens';
import { APP_VERSION } from '@/constants/version';
import type { Capabilities } from '@/platforms/types';

export const HUB_API_BASE =
  ((import.meta.env.VITE_HUB_API_BASE_URL as string | undefined) ?? '').replace(/\/$/, '') ||
  'https://pcb-innovation-api.azurewebsites.net/api';

export class ConsentWaitingError extends Error {
  public constructor() {
    super(CONSENT_WAITING_MESSAGE);
    this.name = 'ConsentWaitingError';
  }
}

export class HubApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'HubApiError';
    this.status = status;
    this.code = code;
  }
}

interface CallOptions {
  query?: Record<string, string | number | undefined | null>;
  body?: unknown;
  /** x-pcb-change-reason. Required by the Hub on every admin write. Must be printable ASCII (sent as is). */
  reason?: string;
}

async function call<T>(method: 'GET' | 'PUT' | 'POST' | 'PATCH' | 'DELETE', path: string, opts: CallOptions = {}): Promise<T> {
  const token = await getPlatformToken();
  if (!token) throw new ConsentWaitingError();

  const url = new URL(`${HUB_API_BASE}/${path.replace(/^\//, '')}`);
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  }
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'x-pcb-client-id': `admin-panel@${APP_VERSION}`,
  };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.reason !== undefined) headers['x-pcb-change-reason'] = opts.reason;

  let resp: Response;
  try {
    resp = await fetch(url.toString(), {
      method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      cache: 'no-store',
    });
  } catch (e) {
    throw new HubApiError(0, `Could not reach the Innovation Hub API: ${e instanceof Error ? e.message : String(e)}`);
  }
  let json: unknown = null;
  try {
    json = await resp.json();
  } catch {
    json = null;
  }
  const obj = (json && typeof json === 'object' ? json : {}) as { success?: boolean; code?: string; error?: string };
  if (!resp.ok || obj.success === false) {
    throw new HubApiError(resp.status, obj.error ?? `The Hub answered ${resp.status}.`, obj.code);
  }
  return json as T;
}

// ── Shapes (contract §GET/PUT routes) ─────────────────────────────────────

export type HubSettingKind = 'upn-list' | 'bool' | 'upn' | 'string' | 'host-suffix-list' | 'json-alias-map' | 'unknown';

export interface HubSetting {
  key: string;
  value: string | null;
  valueTruncated: boolean;
  exists: boolean;
  isActive: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  kind: HubSettingKind | string;
  description: string;
  editable: boolean;
  confirm: 'none' | 'reason' | 'typed' | string;
  section: string;
}

export interface HubSettingPutResult {
  success: boolean;
  key: string;
  changed: boolean;
  before: string | null;
  after: string | null;
  updatedAt: string | null;
}

export interface HubAuditEntry {
  auditId: number;
  route: string;
  actorUpn: string;
  detail: string | null;
  detailTruncated: boolean;
  rowsAffected: number | null;
  outcome: string;
  createdAt: string;
}

export type DeliveryStatusFilter = 'failed' | 'email' | 'legacy' | 'all';

export interface HubDelivery {
  recipientId: number;
  askId: number;
  kind: string | null;
  subject: string | null;
  status: string | null;
  upn: string | null;
  role: string | null;
  lastDeliveryStatus: string | null;
  lastDeliveryDetail: string | null;
  lastDeliveryAt: string | null;
  cardSentAt: string | null;
  cardUpdatedAt: string | null;
  requestedAt: string | null;
}

export interface HubDeliveryCount {
  deliveryStatus: string | null;
  openAsks: number;
  total: number;
}

/** One of the caller's test ask cards (pcb-innovation-api 0.13.0, v3/hub/admin/tools/test-card). */
export interface HubTestCard {
  askId: number;
  token: string | null;
  status: string;
  /** Open | Claimed | Done | Declined | Cancelled | Expired */
  outcome: string;
  claimedBy: string | null;
  claimedAt: string | null;
  settledBy: string | null;
  settledAt: string | null;
  decision: string | null;
  comment: string | null;
  /** 'card' = pressed in Teams; 'app' = answered in an app. */
  channel: string | null;
  sentTo: string | null;
  /** sent | updated | failed */
  deliveryStatus: string | null;
  deliveryDetail: string | null;
  cardSentAt: string | null;
  cardUpdatedAt: string | null;
  requestedAt: string;
  requestedBy: string;
  expired: boolean;
}

export interface HubTestCardList {
  success: boolean;
  sentTo: string | null;
  /** false = the bot has no chat with the caller (or cannot send at all): see code and problem. */
  installed: boolean;
  code: string | null;
  problem: string | null;
  maxOpen: number;
  items: HubTestCard[];
}

export interface HubTestCardSend {
  success: boolean;
  askId: number | null;
  token: string | null;
  sentTo: string | null;
  delivered: number;
  failed: number;
  /** SENT | NOT_DELIVERED */
  code: string;
  message: string;
}

export const hubApi = {
  capabilities: () => call<Capabilities>('GET', 'v3/hub/admin/capabilities'),
  settings: () => call<{ success: boolean; settings: HubSetting[] }>('GET', 'v3/hub/admin/settings'),
  putSetting: (key: string, value: string | boolean, reason: string, confirm?: string) =>
    call<HubSettingPutResult>('PUT', `v3/hub/admin/settings/${encodeURIComponent(key)}`, {
      body: confirm !== undefined ? { value, confirm } : { value },
      reason,
    }),
  audit: (q: { top?: number; route?: string; actor?: string; since?: string; beforeId?: number }) =>
    call<{ success: boolean; entries: HubAuditEntry[] }>('GET', 'v3/hub/admin/audit', { query: q }),
  deliveries: (status: DeliveryStatusFilter, top?: number) =>
    call<{ success: boolean; status: string; deliveries: HubDelivery[]; counts: HubDeliveryCount[] }>(
      'GET',
      'v3/hub/admin/asks/deliveries',
      { query: { status, top } },
    ),
  testCards: () => call<HubTestCardList>('GET', 'v3/hub/admin/tools/test-card'),
  sendTestCard: (reason: string) => call<HubTestCardSend>('POST', 'v3/hub/admin/tools/test-card', { body: {}, reason }),
  cancelTestCard: (askId: number, reason: string) =>
    call<{ success: boolean; code: string; message: string }>('POST', `v3/hub/admin/tools/test-card/${askId}/cancel`, { body: {}, reason }),
};
